import React, { useState, useEffect } from 'react';
import { db, auth, storage } from '../firebase/firebase';
import { collection, getDocs, addDoc, deleteDoc, doc, updateDoc, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, uploadBytesResumable, deleteObject, listAll } from 'firebase/storage';
import { Box, Button, TextField, Typography, List, ListItem, IconButton, MenuItem, Select, FormControl, InputLabel, LinearProgress } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import RecordContentModal from '../components/RecordContentModal';

const AdminPage = () => {
  const [courses, setCourses] = useState([]);
  const [newCourse, setNewCourse] = useState({ name: '', description: '', videoUrl: '', price: '', author: '', image: '', curriculum: [] });
  const [carouselItems, setCarouselItems] = useState([]);
  const [newCarouselItem, setNewCarouselItem] = useState({ title: '', description: '', imageUrl: '' });
  const [newTopic, setNewTopic] = useState({ mainTopic: '', subParts: [] });
  const [newSubPart, setNewSubPart] = useState({ title: '', content: [] });
  const [newContent, setNewContent] = useState({ type: 'video', url: '' });
  const [contentType, setContentType] = useState('video');
  const [uploadProgress, setUploadProgress] = useState(null);
  const [isRecordModalOpen, setIsRecordModalOpen] = useState(false);
  const [isTopicAdded, setIsTopicAdded] = useState(false);
  const [isSubTopicAdded, setIsSubTopicAdded] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCoursesAndCarousel = async () => {
      const coursesCollection = collection(db, 'courses');
      const courseSnapshot = await getDocs(coursesCollection);
      const courseList = courseSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setCourses(courseList);

      const carouselCollection = collection(db, 'carouselItems');
      const carouselSnapshot = await getDocs(carouselCollection);
      const carouselList = carouselSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setCarouselItems(carouselList);
    };
    fetchCoursesAndCarousel();
  }, []);

  const handleAddCourse = async () => {
    if (!newCourse.name || !newCourse.price || !newCourse.author || !newCourse.image) {
      alert("Please fill in all required fields.");
      return;
    }

    if (isNaN(newCourse.price)) {
      alert("Price must be a valid number.");
      return;
    }

    const coursesCollection = collection(db, 'courses');
    await addDoc(coursesCollection, newCourse);
    setNewCourse({ name: '', description: '', videoUrl: '', price: '', author: '', image: '', curriculum: [] });
    setSuccessMessage('Course added successfully!');
    setTimeout(() => setSuccessMessage(''), 3000);
    const courseSnapshot = await getDocs(coursesCollection);
    setCourses(courseSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
  };

  const handleAddTopic = () => {
    setNewCourse({ ...newCourse, curriculum: [...newCourse.curriculum, newTopic] });
    setNewTopic({ mainTopic: '', subParts: [] });
    setIsTopicAdded(true);
    setIsSubTopicAdded(false);
  };

  const handleAddSubPart = () => {
    const updatedCurriculum = newCourse.curriculum.map((topic, index) => {
      if (index === newCourse.curriculum.length - 1) {
        return { ...topic, subParts: [...topic.subParts, newSubPart] };
      }
      return topic;
    });
    setNewCourse({ ...newCourse, curriculum: updatedCurriculum });
    setNewSubPart({ title: '', content: [] });
    setIsSubTopicAdded(true);
  };

  const handleAddContent = async () => {
    if (newContent.type === 'firebaseVideo' && newContent.file) {
      if (newContent.file.size > process.env.REACT_APP_MAX_VIDEO_SIZE) {
        alert("Video file size must be less than 15 MB.");
        return;
      }
      const storageRef = ref(storage, `videos/${newCourse.name}/${newContent.file.name}`);
      const uploadTask = uploadBytesResumable(storageRef, newContent.file);

      uploadTask.on('state_changed', 
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        }, 
        (error) => {
          console.error('Upload failed', error);
        }, 
        async () => {
          const url = await getDownloadURL(uploadTask.snapshot.ref);
          const contentWithoutFile = { ...newContent, url };
          delete contentWithoutFile.file;
          finalizeContentAddition(contentWithoutFile);
        }
      );
    } else if (newContent.type === 'pdf' && newContent.file) {
      if (newContent.file.size > process.env.REACT_APP_MAX_PDF_SIZE) {
        alert("PDF file size must be less than 5 MB.");
        return;
      }
      const storageRef = ref(storage, `pdfs/${newCourse.name}/${newContent.file.name}`);
      const uploadTask = uploadBytesResumable(storageRef, newContent.file);

      uploadTask.on('state_changed', 
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        }, 
        (error) => {
          console.error('Upload failed', error);
        }, 
        async () => {
          const url = await getDownloadURL(uploadTask.snapshot.ref);
          const contentWithoutFile = { ...newContent, url };
          delete contentWithoutFile.file;
          finalizeContentAddition(contentWithoutFile);
        }
      );
    } else {
      finalizeContentAddition(newContent);
    }
  };

  const finalizeContentAddition = (content) => {
    const updatedCurriculum = newCourse.curriculum.map((topic, index) => {
      if (index === newCourse.curriculum.length - 1) {
        const updatedSubParts = topic.subParts.map((subPart, subIndex) => {
          if (subIndex === topic.subParts.length - 1) {
            return { ...subPart, content: [...subPart.content, content] };
          }
          return subPart;
        });
        return { ...topic, subParts: updatedSubParts };
      }
      return topic;
    });
    setNewCourse({ ...newCourse, curriculum: updatedCurriculum });
    setNewContent({ type: 'video', url: '' });
    setUploadProgress(null);
  };

  const handleAddCarouselItem = async () => {
    if (!newCarouselItem.title || !newCarouselItem.description || !newCarouselItem.imageUrl) {
      alert("Please fill in all required fields for the carousel item.");
      return;
    }

    const carouselCollection = collection(db, 'carouselItems');
    await addDoc(carouselCollection, newCarouselItem);
    setNewCarouselItem({ title: '', description: '', imageUrl: '' });
    const carouselSnapshot = await getDocs(carouselCollection);
    setCarouselItems(carouselSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
  };

  const handleDeleteCourse = async (id) => {
    const courseDoc = doc(db, 'courses', id);
    const courseData = await getDoc(courseDoc);
    
    if (courseData.exists()) {
      const courseName = courseData.data().name; 

      // Delete videos folder contents
      const videosRef = ref(storage, `videos/${courseName}`);
      const listResult = await listAll(videosRef);
      const deletePromises = listResult.items.map(fileRef => deleteObject(fileRef));
      await Promise.all(deletePromises).catch((error) => {
        console.error('Error deleting videos:', error);
      });

      // Delete PDFs folder contents
      const pdfsRef = ref(storage, `pdfs/${courseName}`);
      const pdfListResult = await listAll(pdfsRef);
      const deletePdfPromises = pdfListResult.items.map(fileRef => deleteObject(fileRef));
      await Promise.all(deletePdfPromises).catch((error) => {
        console.error('Error deleting PDFs:', error);
      });
    }

    await deleteDoc(courseDoc);
    setCourses(courses.filter(course => course.id !== id));
  };

  const handleDeleteCarouselItem = async (id) => {
    const carouselDoc = doc(db, 'carouselItems', id);
    await deleteDoc(carouselDoc);
    setCarouselItems(carouselItems.filter(item => item.id !== id));
  };

  const handleUpdateCourse = async (id, updatedCourse) => {
    const courseDoc = doc(db, 'courses', id);
    await updateDoc(courseDoc, updatedCourse);
    setCourses(courses.map(course => (course.id === id ? { ...course, ...updatedCourse } : course)));
  };

  const handleUpdateCarouselItem = async (id, updatedItem) => {
    const carouselDoc = doc(db, 'carouselItems', id);
    await updateDoc(carouselDoc, updatedItem);
    setCarouselItems(carouselItems.map(item => (item.id === id ? { ...item, ...updatedItem } : item)));
  };

  const handleMakeTest = (courseName) => {
    const formattedCourseName = courseName.toLowerCase().replace(/\s+/g, '-'); 
    navigate(`/admin/create-test/${formattedCourseName}`);
  };

  const handleUploadSignature = async (courseName, file) => {
    const validTypes = ['image/png'];
    if (!validTypes.includes(file.type)) {
      alert('Please upload a valid PNG image.');
      return;
    }
    
    // Format courseName: decapitalize, remove special characters (including colons), replace with hyphens
    const formattedCourseName = courseName
      .toLowerCase()
      .replace(/[^a-zA-Z]/g, '-') // Remove all non-alphabet characters (including colons) and replace with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with a single hyphen
      .replace(/^-|-$/g, '') // Remove leading and trailing hyphens
      .trim(); // Remove any leading or trailing spaces

    const storageRef = ref(storage, `signatures/${formattedCourseName}/signature.png`);
    await uploadBytes(storageRef, file);
    console.log('Signature uploaded successfully');
  };

  const handleSignatureChange = (courseName) => (event) => {
    const file = event.target.files[0];
    if (file) {
      handleUploadSignature(courseName, file);
    }
  };

  const handleRecordedContent = (content) => {
    finalizeContentAddition(content);
    setIsRecordModalOpen(false);
  };

  return (
    <Box sx={{ padding: 4 }}>
      <Typography variant="h4" gutterBottom>Admin Dashboard</Typography>
      
      <Typography variant="h6">Add New Course</Typography>
      <TextField label="Course Name" value={newCourse.name} onChange={(e) => setNewCourse({ ...newCourse, name: e.target.value })} fullWidth />
      <TextField label="Description" value={newCourse.description} onChange={(e) => setNewCourse({ ...newCourse, description: e.target.value })} fullWidth />
      <TextField label="Video URL (Optional)" value={newCourse.videoUrl} onChange={(e) => setNewCourse({ ...newCourse, videoUrl: e.target.value })} fullWidth />
      <TextField label="Price (Enter Value without Symbol)" value={newCourse.price} onChange={(e) => setNewCourse({ ...newCourse, price: e.target.value })} fullWidth />
      <TextField label="Author" value={newCourse.author} onChange={(e) => setNewCourse({ ...newCourse, author: e.target.value })} fullWidth />
      <TextField label="Thumbnail Image URL (Copy Image Address of any Image from Google)" value={newCourse.image} onChange={(e) => setNewCourse({ ...newCourse, image: e.target.value })} fullWidth />
      
      <Typography variant="h6" sx={{ mt: 4 }}>Add Curriculum</Typography>
      <TextField label="Topic" value={newTopic.mainTopic} onChange={(e) => setNewTopic({ ...newTopic, mainTopic: e.target.value })} fullWidth />
      <Button variant="contained" onClick={handleAddTopic}>Add Topic</Button>
      
      <Typography variant="h6" sx={{ mt: 4 }}>Add Subtopic</Typography>
      <TextField label="Subtopic" value={newSubPart.title} disabled={!isTopicAdded} onChange={(e) => setNewSubPart({ ...newSubPart, title: e.target.value })} fullWidth />
      <Button variant="contained" onClick={handleAddSubPart} disabled={!isTopicAdded}>Add Subtopic</Button>
      
      <Typography variant="h6" sx={{ mt: 4 }}>Add Content</Typography>
      <FormControl fullWidth>
        <InputLabel>Content Type</InputLabel>
        <Select
          value={contentType}
          onChange={(e) => setContentType(e.target.value)}
          disabled={!isSubTopicAdded}
        >
          <MenuItem value="video">Video (YouTube)</MenuItem>
          <MenuItem value="pdf">PDF</MenuItem>
          <MenuItem value="firebaseVideo">Video (Firebase Storage)</MenuItem>
          <MenuItem value="record">Record Content (Audio/Video)</MenuItem>
        </Select>
      </FormControl>
      {contentType === 'record' ? (
        <RecordContentModal
          open={true}
          onClose={() => setContentType('video')}
          onContentRecorded={handleRecordedContent}
          courseName={newCourse.name}
        />
      ) : contentType === 'firebaseVideo' || contentType === 'pdf' ? (
        <>
          <TextField
            type="file"
            onChange={(e) => setNewContent({ ...newContent, type: contentType, file: e.target.files[0] })}
            fullWidth
            disabled={!isSubTopicAdded}
          />
          {uploadProgress !== null && (
            <Box sx={{ width: '100%', mt: 2 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
              {uploadProgress === 100 && <Typography variant="body2" color="success.main">Upload complete!</Typography>}
            </Box>
          )}
        </>
      ) : (
        <TextField
          label="Content URL"
          value={newContent.url}
          onChange={(e) => setNewContent({ ...newContent, type: contentType, url: e.target.value })}
          fullWidth
          disabled={!isSubTopicAdded}
        />
      )}
      <Button variant="contained" onClick={handleAddContent} disabled={!isSubTopicAdded}>Upload Content</Button>

      <Box sx={{ display: 'flex', justifyContent: 'flex-start'}}>
        <Button variant="contained" onClick={handleAddCourse} sx={{ mt: 2, backgroundColor: '#1F7523FF', '&:hover': { backgroundColor: '#1C491EFF' }}}>Finish Adding New Course</Button>
        {successMessage && <Typography variant="body2" color="success.main" sx={{ ml: 2 }}>{successMessage}</Typography>}
      </Box>

      <Typography variant="h6" sx={{ mt: 4 }}>Add New Carousel Item</Typography>
      <TextField label="Title" value={newCarouselItem.title} onChange={(e) => setNewCarouselItem({ ...newCarouselItem, title: e.target.value })} fullWidth />
      <TextField label="Description" value={newCarouselItem.description} onChange={(e) => setNewCarouselItem({ ...newCarouselItem, description: e.target.value })} fullWidth />
      <TextField label="Image URL" value={newCarouselItem.imageUrl} onChange={(e) => setNewCarouselItem({ ...newCarouselItem, imageUrl: e.target.value })} fullWidth />
      <Button variant="contained" onClick={handleAddCarouselItem}>Add Carousel Item</Button>

      <Typography variant="h6" sx={{ mt: 4 }}>Existing Courses</Typography>
      <List>
        {courses.map(course => (
          <ListItem key={course.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography>{course.name}</Typography>
            <Box>
              <IconButton onClick={() => handleDeleteCourse(course.id)} sx={{ ml: 2, mr: 2 }}><DeleteIcon /></IconButton>
              <Button variant="outlined" onClick={() => navigate(`/admin/edit-course/${course.id}`)}>Edit</Button>
              <Button variant="contained" onClick={() => handleMakeTest(course.name)} sx={{ ml: 2, mr: 2 }}>Create Test</Button>
              <Button 
                variant="contained" 
                onClick={() => navigate(`/admin/record-content/${course.id}`)} 
                sx={{ ml: 2, mr: 2 }}
              >
                Record Content
              </Button>
              {/*<input
                type="file"
                accept="image/*"
                onChange={handleSignatureChange(course.name)}
                style={{ marginLeft: '10px' }}
              />*/}
              <Button variant="contained" component="label" sx={{ ml: 1 }}>
                Upload Signature
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleSignatureChange(course.name)}
                />
              </Button>
            </Box>
          </ListItem>
        ))}
      </List>

      <Typography variant="h6" sx={{ mt: 4 }}>Existing Carousel Items</Typography>
      <List>
        {carouselItems.map(item => (
          <ListItem key={item.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography>{item.title}</Typography>
            <Box>
              <IconButton onClick={() => handleDeleteCarouselItem(item.id)}><DeleteIcon /></IconButton>
              <Button onClick={() => navigate(`/admin/edit-carousel/${item.id}`)}>Edit</Button>
            </Box>
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

export default AdminPage;
